'use strict'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import axios from 'axios'
import {
  makeStyles,
  Paper,
  Box,
  Button,
  Typography,
  TextField,
  Select,
  MenuItem,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from '@material-ui/core'
import RefreshIcon from '@material-ui/icons/Refresh'
import CrewDayRouteDetail from './CrewDayRouteDetail'

const toYmd = (value) => {
  const date = new Date(value)
  if (isNaN(date.getTime())) return ''
  return date.toISOString().slice(0, 10)
}

const useStyles = makeStyles(theme => ({
  controlsCard: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  controlsRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1.5),
    alignItems: 'center'
  },
  select: {
    minWidth: 180
  },
  diffCard: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  compareCard: {
    padding: theme.spacing(2)
  },
  chipRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5)
  },
  movedList: {
    marginTop: theme.spacing(1)
  },
  movedItem: {
    fontSize: '0.8rem',
    lineHeight: 1.6
  },
  showMore: {
    fontSize: '0.78rem',
    cursor: 'pointer',
    color: theme.palette.primary.main,
    marginTop: 4
  },
  metricTable: {
    marginTop: theme.spacing(1)
  },
  metricCell: {
    padding: '6px 12px'
  },
  metricHeader: {
    fontWeight: 600,
    backgroundColor: theme.palette.grey[100],
    padding: '8px 12px'
  },
  deltaPositive: {
    color: '#c62828'
  },
  deltaNegative: {
    color: '#2e7d32'
  },
  deltaZero: {
    color: theme.palette.text.secondary
  }
}))

export default function CompareTab ({
  url,
  selectedScenarioId,
  scenarios,
  crews,
  crewIds,
  routes,
  routeIds,
  onSnackbar
}) {
  const classes = useStyles()

  const [compareToScenarioId, setCompareToScenarioId] = useState('')
  const [compareLevel, setCompareLevel] = useState('fleet')
  const [compareCrewId, setCompareCrewId] = useState('')
  const [compareDate, setCompareDate] = useState(() => new Date().toISOString().slice(0, 10))
  const [scenarioCompare, setScenarioCompare] = useState(null)
  const [loadingCompare, setLoadingCompare] = useState(false)
  const [scenarioDiff, setScenarioDiff] = useState(null)
  const [loadingDiff, setLoadingDiff] = useState(false)
  const [recomputingExact, setRecomputingExact] = useState(false)
  const [exactRecompute, setExactRecompute] = useState(null)
  const [showAllMoved, setShowAllMoved] = useState(false)

  // Build route→crew lookup for deriving crewId from selected route
  const routeCrewMap = useMemo(() => {
    const map = {}
    ;(routes || []).forEach(r => { if (r.crewId) map[r._id] = r.crewId })
    return map
  }, [routes])

  useEffect(() => {
    if (compareCrewId) return
    // Prefer first route's crewId; fall back to first crew
    const firstRoute = (routes || [])[0]
    if (firstRoute?.crewId) { setCompareCrewId(firstRoute.crewId); return }
    const firstCrew = crews[0]?._id || ''
    if (firstCrew) setCompareCrewId(firstCrew)
  }, [compareCrewId, crews, routes])

  const fetchScenarioDiff = useCallback(async (scenarioId, { silentError = false } = {}) => {
    if (!scenarioId) { setScenarioDiff(null); return null }
    setLoadingDiff(true)
    try {
      const response = await axios.get(`${url}/api/hmi/territory/scenarios/${scenarioId}/diff`, {
        params: { includeMetricsDelta: true }
      })
      setScenarioDiff(response.data || null)
      return response.data || null
    } catch (err) {
      console.error('Error loading scenario diff:', err)
      if (!silentError) {
        onSnackbar(err.response?.data?.message || err.message || 'Failed to load diff', 'error')
      }
      setScenarioDiff(null)
      return null
    } finally {
      setLoadingDiff(false)
    }
  }, [url, onSnackbar])

  const fetchScenarioCompare = useCallback(async (scenarioId, opts = {}) => {
    if (!scenarioId) { setScenarioCompare(null); return null }
    const level = opts.level || compareLevel
    const crewId = opts.crewId || compareCrewId
    const date = opts.date || compareDate
    const compareTo = opts.compareTo ?? compareToScenarioId

    const params = { level: level || 'fleet' }
    if (compareTo) params.compareToScenarioId = compareTo
    if (params.level !== 'fleet' && crewId) params.crewId = crewId
    if (params.level === 'crew-day') params.date = date || new Date().toISOString().slice(0, 10)

    setLoadingCompare(true)
    try {
      const response = await axios.get(`${url}/api/hmi/territory/scenarios/${scenarioId}/compare`, { params })
      setScenarioCompare(response.data || null)
      return response.data || null
    } catch (err) {
      console.error('Error loading comparison:', err)
      if (!opts.silentError) {
        onSnackbar(err.response?.data?.message || err.message || 'Failed to load comparison', 'error')
      }
      setScenarioCompare(null)
      return null
    } finally {
      setLoadingCompare(false)
    }
  }, [compareCrewId, compareDate, compareLevel, compareToScenarioId, url, onSnackbar])

  useEffect(() => {
    if (!selectedScenarioId) {
      setScenarioDiff(null)
      setScenarioCompare(null)
      setExactRecompute(null)
      return
    }
    setExactRecompute(null)
    fetchScenarioDiff(selectedScenarioId, { silentError: true })
    fetchScenarioCompare(selectedScenarioId, { silentError: true })
  }, [selectedScenarioId, fetchScenarioDiff, fetchScenarioCompare])

  useEffect(() => {
    if (!selectedScenarioId) return
    fetchScenarioCompare(selectedScenarioId, { silentError: true })
  }, [compareCrewId, compareDate, compareLevel, compareToScenarioId, fetchScenarioCompare, selectedScenarioId])

  const runRecomputeExact = async () => {
    if (!selectedScenarioId) return
    const body = {}
    if (compareLevel === 'crew-day') {
      if (!compareCrewId) { onSnackbar('Pick a route first for route-day exact recompute', 'warning'); return }
      body.scope = 'day'
      body.crewId = compareCrewId
      body.date = compareDate || new Date().toISOString().slice(0, 10)
    } else if (compareLevel === 'crew-week') {
      if (!compareCrewId) { onSnackbar('Pick a route first for route-week exact recompute', 'warning'); return }
      body.scope = 'week'
      body.crewId = compareCrewId
      body.weekOf = compareDate || new Date().toISOString().slice(0, 10)
    } else {
      body.scope = 'scenario'
      body.weekOf = compareDate || new Date().toISOString().slice(0, 10)
    }

    setRecomputingExact(true)
    try {
      const response = await axios.post(`${url}/api/hmi/territory/scenarios/${selectedScenarioId}/recompute-exact`, body)
      const payload = response.data || null
      setExactRecompute(payload)

      if (body.scope === 'day' && payload?.recompute?.day) {
        const exactDay = payload.recompute.day
        setScenarioCompare(prev => {
          if (!prev?.comparison || prev.comparison.level !== 'crew-day') return prev
          const currentDate = toYmd(prev.comparison.date)
          const targetDate = toYmd(body.date)
          if (String(prev.comparison.crewId || '') !== String(body.crewId || '') || currentDate !== targetDate) return prev
          const baseline = prev.comparison.baseline || {}
          const scenario = {
            ...prev.comparison.scenario,
            stops: Number(exactDay.stops || 0),
            serviceMin: Number(exactDay.serviceMin || 0),
            driveMin: Number(exactDay.driveMin || 0),
            totalMin: Number(exactDay.totalMin || 0),
            otMin: Number(exactDay.otMin || 0),
            mode: exactDay.mode || 'exact'
          }
          const delta = {}
          Object.keys({ ...baseline, ...scenario }).forEach(key => {
            const sv = Number(scenario[key])
            const bv = Number(baseline[key])
            if (Number.isFinite(sv) && Number.isFinite(bv)) {
              delta[key] = Math.round((sv - bv) * 100) / 100
            }
          })
          return { ...prev, comparison: { ...prev.comparison, scenario, delta } }
        })
      }

      const summary = payload?.recompute?.summary || payload?.recompute?.fleet || null
      const exactDays = summary?.exactDays || 0
      const estimatedDays = summary?.estimatedDays || 0
      const callsAttempted = summary?.orsCallsAttempted || 0
      const callsSucceeded = summary?.orsCallsSucceeded || 0
      onSnackbar(
        `Exact recompute complete (${exactDays} exact / ${estimatedDays} est, ORS ${callsSucceeded}/${callsAttempted})`,
        estimatedDays > 0 ? 'warning' : 'success'
      )
    } catch (err) {
      console.error('Error recomputing exact metrics:', err)
      onSnackbar(err.response?.data?.message || err.message || 'Failed to recompute exact metrics', 'error')
    } finally {
      setRecomputingExact(false)
    }
  }

  const diffSummary = scenarioDiff?.diff?.summary || {}
  const movedItems = scenarioDiff?.diff?.moved || []
  const metricsDelta = scenarioDiff?.metricsDelta || null
  const comparison = scenarioCompare?.comparison || null
  const baselineData = comparison?.baseline || null
  const scenarioData = comparison?.scenario || null
  const deltaData = comparison?.delta || {}

  const deltaClass = (val) => {
    if (val > 0) return classes.deltaPositive
    if (val < 0) return classes.deltaNegative
    return classes.deltaZero
  }

  const formatDelta = (val) => {
    if (val == null || !Number.isFinite(val)) return '-'
    const rounded = Math.round(val)
    if (rounded > 0) return `+${rounded}`
    return String(rounded)
  }

  const exactSummary = useMemo(() => {
    const payload = exactRecompute?.recompute || {}
    return payload.summary || payload.fleet || null
  }, [exactRecompute])

  const metricKeys = useMemo(() => {
    if (!baselineData && !scenarioData) return []
    const numericKeys = ['stops', 'serviceMin', 'driveMin', 'totalMin', 'otMin', 'avgCrewLoad', 'maxCrewLoad', 'totalProperties']
    const allKeys = new Set([...Object.keys(baselineData || {}), ...Object.keys(scenarioData || {})])
    return numericKeys.filter(k => allKeys.has(k))
  }, [baselineData, scenarioData])

  const isLoading = loadingCompare || loadingDiff || recomputingExact

  return (
    <div>
      {/* Controls */}
      <Paper variant="outlined" className={classes.controlsCard}>
        <div className={classes.controlsRow}>
          <Typography variant="body2" style={{ fontWeight: 600 }}>Compare to:</Typography>
          <Select
            value={compareToScenarioId}
            onChange={(e) => setCompareToScenarioId(e.target.value)}
            variant="outlined"
            size="small"
            displayEmpty
            className={classes.select}
          >
            <MenuItem value="">Published (live)</MenuItem>
            {scenarios.filter(item => item._id !== selectedScenarioId).map(item => (
              <MenuItem key={item._id} value={item._id}>{item.name}</MenuItem>
            ))}
          </Select>
          <Typography variant="body2" style={{ fontWeight: 600 }}>Level:</Typography>
          <Select
            value={compareLevel}
            onChange={(e) => setCompareLevel(e.target.value)}
            variant="outlined"
            size="small"
            className={classes.select}
          >
            <MenuItem value="fleet">Fleet</MenuItem>
            <MenuItem value="crew-week">Route Week</MenuItem>
            <MenuItem value="crew-day">Route Day</MenuItem>
          </Select>
          {compareLevel !== 'fleet' && (
            <>
              <Typography variant="body2" style={{ fontWeight: 600 }}>Route:</Typography>
              <Select
                value={compareCrewId}
                onChange={(e) => setCompareCrewId(e.target.value)}
                variant="outlined"
                size="small"
                displayEmpty
                className={classes.select}
              >
                <MenuItem value="" disabled>Select route</MenuItem>
                {(routes && routes.length > 0)
                  ? routes.map(route => (
                    <MenuItem key={route._id} value={route.crewId || route._id}>{route.name}</MenuItem>
                  ))
                  : crews.map(crew => (
                    <MenuItem key={crew._id} value={crew._id}>{crew.name}</MenuItem>
                  ))
                }
              </Select>
            </>
          )}
          {compareLevel === 'crew-day' && (
            <TextField
              type="date"
              value={compareDate}
              onChange={(e) => setCompareDate(e.target.value)}
              variant="outlined"
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          )}
          <Button
            variant="outlined"
            size="small"
            disabled={!selectedScenarioId || isLoading}
            onClick={runRecomputeExact}
          >
            {recomputingExact ? 'Recomputing...' : 'Recompute Exact'}
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshIcon />}
            disabled={!selectedScenarioId || isLoading}
            onClick={() => {
              fetchScenarioDiff(selectedScenarioId)
              fetchScenarioCompare(selectedScenarioId)
            }}
          >
            Refresh
          </Button>
          {isLoading && <CircularProgress size={18} />}
        </div>
      </Paper>

      {/* Diff Summary */}
      <Paper variant="outlined" className={classes.diffCard}>
        <Typography variant="subtitle1" gutterBottom style={{ fontWeight: 600 }}>Diff Summary (vs published)</Typography>
        {scenarioDiff?.diff?.summary ? (
          <>
            <div className={classes.chipRow}>
              <Chip size="small" label={`Moved: ${diffSummary.movedCount || 0}`} />
              <Chip size="small" label={`VIP changed: ${diffSummary.vipChangedCount || 0}`} />
              <Chip size="small" label={`Locks changed: ${diffSummary.lockChangedCount || 0}`} />
            </div>
            {metricsDelta && (
              <div className={classes.chipRow}>
                <Chip
                  size="small"
                  label={`OT delta: ${Math.round(metricsDelta.totalOTMinutesDelta || 0)} min`}
                  style={{
                    backgroundColor: (metricsDelta.totalOTMinutesDelta || 0) <= 0 ? '#e8f5e9' : '#fce4ec',
                    color: (metricsDelta.totalOTMinutesDelta || 0) <= 0 ? '#2e7d32' : '#c62828'
                  }}
                />
                <Chip size="small" label={`Avg load delta: ${Math.round(metricsDelta.avgCrewLoadDelta || 0)} min`} />
              </div>
            )}
            {movedItems.length > 0 && (
              <div className={classes.movedList}>
                <Typography variant="body2" style={{ fontWeight: 600 }}>Moved properties:</Typography>
                {(showAllMoved ? movedItems : movedItems.slice(0, 5)).map(item => (
                  <Typography key={item.aspirePropertyId} className={classes.movedItem}>
                    {'\u2022'} Aspire #{item.aspirePropertyId}: {item.fromCrewName || 'Unassigned'} {'\u2192'} {item.toCrewName || 'Unassigned'}
                  </Typography>
                ))}
                {movedItems.length > 5 && !showAllMoved && (
                  <Typography className={classes.showMore} onClick={() => setShowAllMoved(true)}>
                    Show all {movedItems.length}...
                  </Typography>
                )}
              </div>
            )}
          </>
        ) : (
          <Typography variant="body2" color="textSecondary">
            {loadingDiff ? 'Loading diff...' : 'No diff loaded yet.'}
          </Typography>
        )}
      </Paper>

      {/* Comparison */}
      <Paper variant="outlined" className={classes.compareCard}>
        <Typography variant="subtitle1" gutterBottom style={{ fontWeight: 600 }}>Comparison</Typography>
        {comparison && (
          <div className={classes.chipRow}>
            <Chip size="small" label={`Level: ${comparison.level}`} />
            <Chip size="small" label={`Baseline: ${scenarioCompare?.compareTo?.name || 'Published (live)'}`} />
            {comparison.crewName && <Chip size="small" label={`Crew: ${comparison.crewName}`} />}
            {comparison.level === 'crew-day' && (
              <>
                <Chip size="small" variant="outlined" label={`Scenario mode: ${scenarioData?.mode || 'estimated'}`} />
                <Chip size="small" variant="outlined" label={`Baseline mode: ${baselineData?.mode || 'estimated'}`} />
              </>
            )}
          </div>
        )}
        {metricKeys.length > 0 && (
          <Table className={classes.metricTable} size="small">
            <TableHead>
              <TableRow>
                <TableCell className={classes.metricHeader}>Metric</TableCell>
                <TableCell className={classes.metricHeader}>Scenario</TableCell>
                <TableCell className={classes.metricHeader}>Baseline</TableCell>
                <TableCell className={classes.metricHeader}>Delta</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {metricKeys.map(key => {
                const sv = scenarioData?.[key]
                const bv = baselineData?.[key]
                const dv = deltaData?.[key]
                return (
                  <TableRow key={key}>
                    <TableCell className={classes.metricCell}>{key}</TableCell>
                    <TableCell className={classes.metricCell}>{sv != null ? Math.round(sv) : '-'}</TableCell>
                    <TableCell className={classes.metricCell}>{bv != null ? Math.round(bv) : '-'}</TableCell>
                    <TableCell className={`${classes.metricCell} ${deltaClass(dv)}`} style={{ fontWeight: 600 }}>
                      {formatDelta(dv)}
                      {key === 'otMin' && dv > 0 && ' \u26A0'}
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        )}
        {comparison && scenarioData?.mode && (
          <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginTop: 8 }}>
            Mode: {scenarioData.mode}{exactSummary ? ` | Last exact: ${exactSummary.exactDays || 0} exact / ${exactSummary.estimatedDays || 0} est` : ' | Last exact: never'}
          </Typography>
        )}
        {!comparison && !loadingCompare && (
          <Typography variant="body2" color="textSecondary">
            No comparison loaded yet.
          </Typography>
        )}
      </Paper>

      {/* Crew-Day Route Detail */}
      {compareLevel === 'crew-day' && compareCrewId && (
        <Box mt={2}>
          <CrewDayRouteDetail
            url={url}
            selectedScenarioId={selectedScenarioId}
            crewId={compareCrewId}
            crewName={crews.find(c => c._id === compareCrewId)?.name || compareCrewId}
            date={compareDate}
            crewIds={crewIds || []}
            onSnackbar={onSnackbar}
          />
        </Box>
      )}
    </div>
  )
}
